import { Sorter } from '@common/services';
import React, { ReactNode, useMemo } from 'react';
import { FcTableHeader, FcTableHeaderProps } from './FcTableHeader';
import { groupArrayValuesByKeys } from './utils/array/group-array-values-by-keys';
import './fc-table.css';

export const FcTable = <T extends {} = any>({
                                              columnsConfig,
                                              rowConfig,
                                              data,
                                              sorter,
                                              onSorterChange,
                                              fixedLayout,
                                            }: FcTableProps<T>) => {

  const { selectionConfig = {}, outlineOnHover } = rowConfig ?? {};
  const {
    fieldId,
    selection,
    globalCheckbox,
    singleCheckbox,
    onSelectionChange,
  } = selectionConfig as FcTableSelectionConfig<any, any>;

  const fields = columnsConfig.map(({ field }) => field);
  if (fieldId) fields.push(fieldId as keyof T);
  const uniqueFields = [...new Set(fields).values()];

  const groupedData: Record<string, any[]> = useMemo(() => groupArrayValuesByKeys(data, uniqueFields, ''), [data]);
  const fieldIds: FieldIdType[] = groupedData[fieldId] ?? [];

  const handleGlobalCheckChange = (checked) => {
    const selectionCopy = new Set(selection ?? []);

    fieldIds.forEach((id) => {
      if (checked) selectionCopy.add(id);
      else selectionCopy.delete(id);
    });

    onSelectionChange?.(selectionCopy);
  };

  const handleSingleCheckChange = (checked, valueId) => {
    const selectionCopy = new Set(selection ?? []);

    if (checked) selectionCopy.add(valueId);
    else selectionCopy.delete(valueId);

    onSelectionChange?.(selectionCopy);
  };

  const globalCheckboxJsx = (
    <input type="checkbox"
           className={'fc-checkbox'}
           id={'checkbox-0'}
           checked={fieldIds.every((id) => selection?.has(id)) ?? false}
           onChange={(ev) => handleGlobalCheckChange(ev.target.checked)} />
  );

  const singleCheckboxJsx = (valueId) => (
    <input type="checkbox"
           className={'fc-checkbox'}
           checked={selection?.has(valueId)}
           onChange={(ev) => handleSingleCheckChange(ev.target.checked, valueId)} />
  );

  return (
    <div className={'w-full overflow-x-auto'}>

      <table className={'fc-table'} style={(fixedLayout) ? { tableLayout: 'fixed' } : {}}>

        {/*Les colonnes*/}
        <colgroup>
          {columnsConfig.map(({ maxWidth = 'auto' }, index) => <col key={index} style={{
            maxWidth,
            width: maxWidth,
          }} />)}
        </colgroup>

        {/*Les headers*/}
        <thead>
        <tr style={{ textAlign: 'left' }}>

          {(globalCheckbox && data.length > 0) && (
            <th className={'text-center'}>{globalCheckboxJsx}</th>
          )}

          {((singleCheckbox && data.length === 0) || (!globalCheckbox && singleCheckbox)) && (
            <th></th>
          )}

          {columnsConfig.map(({ header, field }, index) => (
            <th key={`${header.title}-${index}`}>
              <FcTableHeader title={header.title}
                             sorting={header.sorting}
                             field={field}
                             activeSorter={sorter}
                             direction={(sorter?.field === field) ? sorter.direction : null}
                             onSorterDirectionChange={(direction) => onSorterChange?.(direction && {
                               field: header.sortingKey || field,
                               direction,
                             })}
              />
            </th>
          ))}
        </tr>
        </thead>

        {/*Le footer*/}
        <tfoot>
        {(data.length === 0) && (
          <tr>
            <th colSpan={20}><span style={{ color: '#929292' }}>Aucune donnée à ce jour</span></th>
          </tr>
        )}
        </tfoot>

        {/*Les lignes*/}
        <tbody>
        {data.map((rowData, rowIndex) => {
          if (selectionConfig && fieldId && !rowData[fieldId]) console.error(`Invalid fieldId "${fieldId}". Please choose one from the data object.`);
          const valueId = rowData[fieldId];

          const outlineOnHoverClasses = (outlineOnHover) ? 'hover:ring-1 hover:ring-[#0063cb] hover:ring-inset hover:ring-offset-1 hover:rounded-lg' : '';

          return <tr key={`${valueId}-${rowIndex}`} className={`relative ${outlineOnHoverClasses} group`}>

            {(singleCheckbox) && (
              <td className={'text-center'}>{singleCheckboxJsx(valueId)}</td>
            )}

            {(!singleCheckbox && globalCheckbox) && (
              <td></td>
            )}

            {columnsConfig.map(({ field, render, definedOnly, displayOnHover }, colIndex) => {
              const fieldValues = groupedData[field];
              const value = fieldValues?.[rowIndex];
              const finalValue = (definedOnly && (value === undefined || value === null))
                ? undefined
                : render?.(value, rowData, rowIndex) ?? value;

              const displayOnHoverClasses = (displayOnHover) ? 'opacity-0 group-hover:opacity-100' : '';

              return <td key={`${valueId}-${rowIndex}-${colIndex}`} className={displayOnHoverClasses}>{finalValue}</td>;
            })}
          </tr>;
        })}
        </tbody>
      </table>
    </div>
  );
};

interface FcTableProps<T extends Record<any, any>> {
  columnsConfig: FcTableColumnConfig<T>[];
  rowConfig?: FcTableRowConfig<T>;
  /**
   * Liste de données
   */
  data: T[];
  /**
   * Tri de la colonne sélectionnée
   */
  sorter?: Sorter | null;
  onSorterChange?: (sorter: Sorter | null) => void,
  // pageConfig?: FcTablePageConfig;
  fixedLayout?: boolean;
}

export interface FcTablePageConfig {
  current: number;
  total: number;
  onChange?: (page: number) => void;
}

export interface FcTableColumnConfig<T extends {} = {}> {
  header: Omit<FcTableHeaderProps, 'onSorterDirectionChange' | 'activeSorter' | 'direction' | 'field'>;
  /**
   * Propriété de la donnée à mapper sur la column
   */
  field: keyof T;
  /**
   * true: si la valeur est undefined ou null, la cellule restera vide et la fonction transform sera ignorée.
   * @default false
   */
  definedOnly?: boolean;
  /**
   * Change l'affichage de la cellule
   * @param value - valeur de la cellule
   * @param element - élément correspondant à la ligne actuelle
   * @param index - index de la ligne actuelle
   */
  render?: (value: any, element: T, index: number) => ReactNode;
  /**
   * Affiche la valeur de la cellule au survol de la ligne
   * @default false
   */
  displayOnHover?: boolean;
  /**
   * Largeur max de la colonne
   */
  maxWidth?: string;
}

export interface FcTableRowConfig<T extends {} = {}> {
  selectionConfig?: FcTableSelectionConfig<T>;
  outlineOnHover?: boolean;
}

export interface FcTableSelectionConfig<T extends {} = {}, K extends keyof T = T[0]> {
  /**
   * Propriété de la valeur unique de la donnée qui sera remontée lors de la sélection des lignes de la table
   */
  fieldId: keyof T;
  /**
   * Activer une checkbox individuelle sur chaque ligne
   * @default false
   */
  singleCheckbox?: boolean;
  /**
   * Activer la checkbox globale qui sélectionne toutes les lignes
   * @default false
   */
  globalCheckbox?: boolean;
  /**
   * Sélection par défaut
   */
  selection?: Set<any>;
  /**
   * Output pour écouter les lignes sélectionnées
   * @param selection
   */
  onSelectionChange?: (selection: Set<any>) => void;
}

type FieldIdType = number | string | any;